// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useState } from "react";
import { SmartLink } from "@/components/SmartLink";

interface NavTreeItem {
  id: number;
  label: string;
  href: string;
  isExternal: boolean;
  children: { id: number; label: string; href: string; isExternal: boolean }[];
}

/**
 * Mobile-friendly nav renderer used inside the header shell's expandable
 * panel. Touch devices have no hover, so child menus use a tap-to-expand
 * accordion (the carat button is separate from the parent link so tapping
 * the label still navigates). Tapping any link inside the panel fires
 * `onNavigate` so the shell can collapse itself.
 */
export function NavLinksMobile({
  items,
  onNavigate,
}: {
  items: NavTreeItem[];
  onNavigate?: () => void;
}) {
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-4 text-sm uppercase tracking-widest">
      {items.map((item) =>
        item.children.length > 0 ? (
          <div key={item.id}>
            <div className="flex items-center justify-between gap-2">
              <SmartLink
                href={item.href}
                onClick={onNavigate}
                className="hover:text-stone-600 transition-colors"
                {...(item.isExternal
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {item.label}
              </SmartLink>
              <button
                type="button"
                onClick={() => setOpenId(openId === item.id ? null : item.id)}
                aria-expanded={openId === item.id}
                aria-label={`Toggle ${item.label} submenu`}
                className="p-2 -m-2 hover:text-stone-600 transition-colors"
              >
                <span className="text-xs">
                  {openId === item.id ? "▲" : "▼"}
                </span>
              </button>
            </div>
            {openId === item.id && (
              <div className="mt-2 pl-4 flex flex-col gap-2">
                {item.children.map((child) => (
                  <SmartLink
                    key={child.id}
                    href={child.href}
                    onClick={onNavigate}
                    className="hover:text-stone-600 transition-colors"
                    {...(child.isExternal
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {child.label}
                  </SmartLink>
                ))}
              </div>
            )}
          </div>
        ) : (
          <SmartLink
            key={item.id}
            href={item.href}
            onClick={onNavigate}
            className="hover:text-stone-600 transition-colors"
            {...(item.isExternal
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {item.label}
          </SmartLink>
        )
      )}
    </div>
  );
}
